import { useEffect, useState, type FormEvent, type ReactElement } from 'react'
import { useAdminSettings, useUpdateSettings } from '@/api/queries'
import type { UpdateSettingsRequest } from '@/api/types'
import { Button, Checkbox, Select, Spinner, TextField, useToast } from '@/components/ui'
import { AdminPage, Card, ErrorNotice } from './AdminLayout'
import styles from './admin.module.css'

type Provider = 'brevo' | 'sendgrid' | 'mailgun' | 'postmark' | 'none'

export function NotificationsPage(): ReactElement {
  const query = useAdminSettings()
  const update = useUpdateSettings()
  const toast = useToast()
  const settings = query.data
  const [push, setPush] = useState(false)
  const [previews, setPreviews] = useState(false)
  const [provider, setProvider] = useState<Provider>('none')
  const [from, setFrom] = useState('')
  const [apiKey, setApiKey] = useState('')

  useEffect(() => {
    if (!settings) return
    setPush(settings.push_enabled)
    setPreviews(settings.push_previews_enabled === true)
    setProvider(settings.email_provider === 'smtp' ? 'none' : settings.email_provider ?? 'none')
    setFrom(settings.email_from ?? '')
  }, [settings])

  if (query.isError) return <AdminPage title="Notifications"><ErrorNotice error={query.error} title="Notification settings could not be loaded" /></AdminPage>
  if (query.isPending || !settings) return <AdminPage title="Notifications"><Spinner size="lg" label="Loading notification settings" /></AdminPage>

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const changes: UpdateSettingsRequest = { push_enabled: push, push_previews_enabled: previews, email_provider: provider, email_from: from.trim() || null }
    if (apiKey.trim()) changes.email_api_key = apiKey.trim()
    update.mutate(changes, {
      onSuccess: () => { setApiKey(''); toast.success('Notification settings saved.') },
      onError: () => toast.error('The notification settings could not be saved.'),
    })
  }

  return (
    <AdminPage title="Notifications" lead="Configure push delivery and the default provider for transactional email.">
      <form onSubmit={submit}>
        <Card title="Push notifications" lead="Apple and Android devices use the relaii Push Service.">
          <div className={styles.stack}>
            <Checkbox label="Enable push notifications" checked={push} onChange={(event) => setPush(event.target.checked)} hint="The server registers itself with the gateway and stores only opaque device ids." />
            <Checkbox label="Show the sender and message text in push notifications" checked={previews} onChange={(event) => setPreviews(event.target.checked)} hint="One choice for everyone in this Space. The text is encrypted for the device that receives it; the gateway, Apple and Google only ever see the ciphertext. When off, a notification shows the Space's name and a generic line." />
          </div>
        </Card>
        <Card title="Email notifications" lead="Used for invitations, password resets and other transactional email.">
          <div className={styles.stack}>
            <Select label="Default email provider" value={provider} onChange={(event) => setProvider(event.target.value as Provider)} options={[
              { value: 'brevo', label: 'Brevo' },
              { value: 'sendgrid', label: 'SendGrid' },
              { value: 'mailgun', label: 'Mailgun' },
              { value: 'postmark', label: 'Postmark' },
              { value: 'none', label: 'Disabled' },
            ]} />
            {provider !== 'none' ? <>
              <TextField label="Sender email" type="email" value={from} maxLength={254} labelExtra={`${from.length}/254`} onChange={(event) => setFrom(event.target.value)} />
              <TextField label={`${provider.charAt(0).toUpperCase()}${provider.slice(1)} API key`} type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder={settings.email_api_key_configured ? 'Configured — enter a new key to replace it' : 'Paste API key'} hint="Stored encrypted on the server." />
            </> : null}
            <div className={styles.cardFoot}><Button type="submit" variant="primary" loading={update.isPending}>Save notification settings</Button></div>
          </div>
        </Card>
      </form>
    </AdminPage>
  )
}
